// apps/prompts/src/hooks/useCollections.ts
import { toast } from '@neuroforge/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  fetchCollections,
  fetchCollectionBySlug,
  fetchSharedCollection,
  createCollection,
  updateCollection,
  deleteCollection,
  addCollectionItem,
  removeCollectionItem,
  reorderCollectionItems,
  enableCollectionSharing,
  revokeCollectionSharing,
} from '../lib/api-prompts'

// ─── Query key factory ───
const collectionKeys = {
  all: ['collections'] as const,
  lists: () => [...collectionKeys.all, 'list'] as const,
  detail: (slug?: string) => [...collectionKeys.all, 'detail', slug] as const,
}

// ─── List user collections ───
export function useCollections() {
  return useQuery({
    queryKey: collectionKeys.lists(),
    queryFn: fetchCollections,
    staleTime: 30_000,
  })
}

// ─── Collection detail by slug ───
export function useCollectionDetail(slug?: string) {
  return useQuery({
    queryKey: collectionKeys.detail(slug),
    queryFn: () => fetchCollectionBySlug(slug!),
    enabled: Boolean(slug),
    staleTime: 30_000,
  })
}

// ─── Create collection ───
export function useCreateCollection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createCollection,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionKeys.lists() })
    },
    onError: () => {
      toast({ type: 'error', message: 'Erreur lors de la création de la collection' })
    },
  })
}

// ─── Update collection ───
export function useUpdateCollection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateCollection>[1] }) =>
      updateCollection(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionKeys.all })
    },
    onError: () => {
      toast({ type: 'error', message: 'Erreur lors de la mise à jour de la collection' })
    },
  })
}

// ─── Delete collection ───
export function useDeleteCollection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteCollection,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionKeys.lists() })
    },
    onError: () => {
      toast({ type: 'error', message: 'Erreur lors de la suppression de la collection' })
    },
  })
}

// ─── Add item to collection ───
export function useAddCollectionItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { collectionId: string; promptId: string; note?: string }) =>
      addCollectionItem(args.collectionId, args.promptId, args.note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionKeys.all })
    },
    onError: () => {
      toast({ type: 'error', message: "Erreur lors de l'ajout à la collection" })
    },
  })
}

// ─── Remove item from collection ───
export function useRemoveCollectionItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { collectionId: string; promptId: string }) =>
      removeCollectionItem(args.collectionId, args.promptId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionKeys.all })
    },
    onError: () => {
      toast({ type: 'error', message: 'Erreur lors du retrait du prompt' })
    },
  })
}

// ─── Reorder items ───
export function useReorderCollectionItems() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { collectionId: string; items: Array<{ itemId: string }> }) =>
      reorderCollectionItems(args.collectionId, args.items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionKeys.all })
    },
    onError: () => {
      toast({ type: 'error', message: 'Erreur lors de la réorganisation' })
    },
  })
}

// ─── Shared collection by token ───
export function useSharedCollection(token?: string) {
  return useQuery({
    queryKey: [...collectionKeys.all, 'shared', token],
    queryFn: () => fetchSharedCollection(token!),
    enabled: Boolean(token),
    staleTime: 60_000,
  })
}

// ─── Enable sharing ───
export function useEnableSharing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: enableCollectionSharing,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionKeys.all })
    },
    onError: () => {
      toast({ type: 'error', message: `Erreur lors de l'activation du partage` })
    },
  })
}

// ─── Revoke sharing ───
export function useRevokeSharing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: revokeCollectionSharing,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionKeys.all })
    },
    onError: () => {
      toast({ type: 'error', message: 'Erreur lors de la révocation du partage' })
    },
  })
}
